import api from '@/services/api';
import { PaginatedResponse } from '@/types/common';
import {
  TimelineType,
  TimelinePost,
  CreateTimelinePost,
  UpdateTimelinePost,
  TimelineQueryParams,
} from '@/types/post';

export type {
  TimelinePost,
  CreateTimelinePost,
  UpdateTimelinePost,
  TimelineQueryParams,
};

export const timelineService = {
  async getAll(params: TimelineQueryParams): Promise<PaginatedResponse<TimelinePost>> {
    const queryParams = new URLSearchParams();
    
    const offset = params.offset ?? 0;
    const limit = params.limit ?? 10;
    
    queryParams.append('offset', offset.toString());
    queryParams.append('limit', limit.toString());
    
    if (params.type) {
      queryParams.append('type', params.type);
    }
    
    if (params.q) {
      queryParams.append('q', params.q);
    }
    
    if (params.territories) {
      const territoriesArray = params.territories.split(',').filter(Boolean);
      territoriesArray.forEach(territory => {
        queryParams.append('territories', territory);
      });
    }
    
    if (params.contexts) {
      const contextsArray = params.contexts.split(',').filter(Boolean);
      contextsArray.forEach(context => {
        queryParams.append('contexts', context);
      });
    }
    
    if (params.topics) {
      const topicsArray = params.topics.split(',').filter(Boolean);
      topicsArray.forEach(topic => {
        queryParams.append('topics', topic);
      });
    }
    
    if (params.partners) {
      const partnersArray = params.partners.split(',').filter(Boolean);
      partnersArray.forEach(partner => {
        queryParams.append('partners', partner);
      });
    }
    
    if (params.sources) {
      const sourcesArray = params.sources.split(',').filter(Boolean);
      sourcesArray.forEach(source => {
        queryParams.append('sources', source);
      });
    }
    
    if (typeof params.favorited === 'boolean') {
      queryParams.append('favorited', params.favorited ? 'true' : 'false');
    }
    
    if (params.created_by) {
      queryParams.append('created_by', params.created_by);
    }
    
    if (params.date_range) {
      queryParams.append('date_range', params.date_range);
    }
    
    if (params.start_date) {
      queryParams.append('start_date', params.start_date);
    }
    
    if (params.end_date) {
      queryParams.append('end_date', params.end_date);
    }
    
    const url = `/timeline?${queryParams.toString()}`;
    const response = await api.get<PaginatedResponse<TimelinePost>>(url);
    return response.data;
  },

  async approve(postId: string): Promise<void> {
    await api.post(`/timeline/${postId}/approve`);
  },

  async reject(postId: string): Promise<void> {
    await api.post(`/timeline/${postId}/reject`);
  },

  async create(data: CreateTimelinePost): Promise<TimelinePost> {
    const response = await api.post<TimelinePost>('/timeline', data);
    return response.data;
  },

  async update(postId: string, data: UpdateTimelinePost): Promise<TimelinePost> {
    const response = await api.put<TimelinePost>(`/timeline/${postId}`, data);
    return response.data;
  },

  async pin(postId: string): Promise<void> {
    await api.post(`/timeline/${postId}/pin`);
  },

  async favorite(postId: string): Promise<void> {
    await api.post(`/timeline/${postId}/favorite`);
  },
};

